import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于久久在线',
  description: '久久在线是一个综合性在线内容平台，提供视频、资讯、娱乐、科技、生活、教育、体育、音乐等八大板块的优质内容。',
};

const orgJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '久久在线',
  url: 'https://jiujiuonline.com',
  description: '久久在线是一个综合性在线内容平台，提供视频、资讯、娱乐、科技等八大板块的优质内容。',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: 'contact@jiujiuonline.com',
    availableLanguage: ['Chinese'],
  },
  sameAs: [
    'https://weibo.com/jiujiuonline',
    'https://space.bilibili.com/jiujiuonline',
  ],
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9rdtmue3oq4jqumu07g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后重试';
        if (msg.includes('spam') || msg.includes('form data is spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后重试');
        } else {
          setFormStatus('error');
          setFormError(msg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  const milestones = [
    { year: '2018', label: '平台创立', desc: '久久在线正式上线，以视频内容为起点，开启在线内容服务之旅。' },
    { year: '2019', label: '用户破百万', desc: '上线仅一年，久久在线注册用户突破100万，内容覆盖三大板块。' },
    { year: '2020', label: '内容大拓展', desc: '新增资讯、科技、生活板块，内容总量突破50万条，日活用户超过200万。' },
    { year: '2022', label: '用户破千万', desc: '注册用户突破1000万，成为中文互联网最具影响力的内容平台之一。' },
    { year: '2024', label: '全面升级', desc: '平台全面升级，新增教育、体育、音乐板块，覆盖八大内容领域。' },
    { year: '2026', label: '用户突破5000万', desc: '活跃用户突破5000万，内容总量超过100万条，稳居行业领先地位。' },
  ];

  return (
    <>
      <SEOHead
        title="关于我们 - 久久在线"
        description="久久在线是一个综合性在线内容平台，创立于2018年，提供视频、资讯、娱乐、科技、生活、教育、体育、音乐等八大板块的优质内容，服务超过5000万用户。"
        keywords="久久在线,关于久久在线,久久在线品牌,久久在线联系方式,久久在线平台介绍"
        canonicalUrl="/about"
        ogType="website"
        jsonLd={[orgJsonLd, aboutJsonLd]}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <div className="px-4 md:px-6 py-8 bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="px-4 md:px-6 py-16 md:py-24">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-6">
              关于久久在线
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-3xl mx-auto">
              久久在线创立于2018年，是一个综合性在线内容平台。我们致力于为用户提供最优质、最全面的在线内容服务，
              涵盖视频、资讯、娱乐、科技、生活、教育、体育、音乐八大板块，让每一位用户都能随时随地畅享精彩内容。
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="px-4 md:px-6 py-12 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { value: '5000万+', label: '活跃用户' },
                { value: '100万+', label: '优质内容' },
                { value: '8', label: '内容板块' },
                { value: '99.9%', label: '服务稳定率' },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <p className="font-heading text-2xl md:text-3xl text-primary-500">{stat.value}</p>
                  <p className="mt-2 text-sm text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Development Timeline */}
        <section className="px-4 md:px-6 py-16 md:py-24">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 text-center mb-14">发展历程</h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-200"></div>
              <div className="space-y-10">
                {milestones.map((m, i) => (
                  <div key={m.year} className={`relative flex items-start gap-6 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} flex-row`}>
                    <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'} pl-12 md:pl-0 md:pr-0 ${i % 2 === 0 ? 'md:pr-12' : 'md:pl-12'}`}>
                      <div className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                        <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-2">
                          {m.year}
                        </span>
                        <h3 className="font-semibold text-foreground-900 text-base mb-1">{m.label}</h3>
                        <p className="text-sm text-foreground-500 leading-relaxed">{m.desc}</p>
                      </div>
                    </div>
                    <div className="hidden md:block flex-1"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="px-4 md:px-6 py-16 md:py-24 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 text-center mb-14">核心价值观</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-shield-star-line', title: '品质优先', desc: '严格把控内容质量，确保每一条内容都经过精心筛选和审核，为用户提供最优质的内容体验。' },
                { icon: 'ri-user-heart-line', title: '用户至上', desc: '以用户需求为导向，持续优化产品体验，让每一位用户都能轻松找到自己感兴趣的内容。' },
                { icon: 'ri-rocket-line', title: '技术创新', desc: '持续投入技术研发，采用先进的流媒体和AI技术，为用户提供稳定、高速、智能的服务。' },
                { icon: 'ri-team-line', title: '合作共赢', desc: '与优质内容创作者建立深度合作，共同打造健康、繁荣的内容生态，实现平台与创作者的双赢。' },
              ].map((val) => (
                <div key={val.title} className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-accent-100 flex items-center justify-center">
                    <i className={`${val.icon} text-2xl text-accent-600`}></i>
                  </div>
                  <h3 className="font-semibold text-foreground-900 mb-2">{val.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{val.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="px-4 md:px-6 py-16 md:py-24">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 text-center mb-4">联系我们</h2>
            <p className="text-center text-foreground-500 mb-14">
              有任何问题或建议？欢迎通过以下方式联系我们，我们将尽快回复
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div className="space-y-6">
                <div className="flex items-start gap-4 p-5 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="w-11 h-11 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-lg text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground-900 text-sm mb-1">电子邮箱</h3>
                    <p className="text-sm text-foreground-500">contact@jiujiuonline.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-5 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="w-11 h-11 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-lg text-accent-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground-900 text-sm mb-1">客服电话</h3>
                    <p className="text-sm text-foreground-500">400-888-9999</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-5 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="w-11 h-11 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-lg text-secondary-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground-900 text-sm mb-1">公司地址</h3>
                    <p className="text-sm text-foreground-500">北京市海淀区中关村科技园区久久在线大厦</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-5 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="w-11 h-11 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-lg text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground-900 text-sm mb-1">工作时间</h3>
                    <p className="text-sm text-foreground-500">周一至周五 9:00 - 18:00</p>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                <h3 className="font-semibold text-foreground-900 mb-5">发送消息</h3>
                <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      id="contact-subject"
                      name="subject"
                      type="text"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入消息主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      消息 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="请输入您的消息（最多500字）"
                    ></textarea>
                  </div>

                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />

                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {formStatus === 'loading' ? '发送中...' : '发送消息'}
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-accent-600 text-center font-medium">消息已发送成功，我们将尽快回复您！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="px-4 md:px-6 py-14 bg-background-100">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-2xl text-foreground-950 mb-4">加入久久在线大家庭</h2>
            <p className="text-foreground-500 mb-6">随时随地畅享优质内容，立即开启您的在线之旅</p>
            <Link
              to="/"
              className="inline-flex px-8 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}